import * as XLSX from "xlsx";
import { ITeacherTrainingReportRow } from "../teacherTrainingReportRow.types";

/**
 * Builds and downloads the "data dump" report using SheetJS (`xlsx`), matching
 * the app's existing convention.
 *
 * IMPORTANT LIMITATION: the free/community `xlsx` package does NOT write
 * cell styles (fill colours, bold, borders) to the output file — style
 * writing is a Pro-only SheetJS feature. This function produces the correct
 * column order/headers/widths from your screenshot, but the header row will
 * NOT be coloured blue/yellow/white/green when opened in Excel/Sheets.
 *
 * If the colour bands are a hard requirement, you have two options:
 *   1. Swap to `xlsx-js-style` (drop-in replacement, same API, adds `cell.s`
 *      style support) — see the commented block at the bottom of this file.
 *   2. Have the backend generate the styled file and have this function just
 *      trigger a blob download instead (ask if you want that version).
 */

const COLUMNS: {
  header: string;
  key: keyof ITeacherTrainingReportRow;
  width: number;
}[] = [
  { header: "Training Title", key: "trainingTitle", width: 22 },
  { header: "Training Start Date", key: "start_date", width: 14 },
  { header: "Training End Date", key: "end_date", width: 14 },
  { header: "Date", key: "trainingDate", width: 14 },
  { header: "District", key: "district", width: 16 },
  { header: "Block", key: "block", width: 16 },
  { header: "Training Batch", key: "training_Batch", width: 16 },
  { header: "Name of the teacher", key: "nameoftheteacher", width: 22 },
  { header: "Employee Code", key: "employeeCode", width: 16 },
  { header: "Teacher Contact Number", key: "teacherMobileNo", width: 20 },
  { header: "School Name", key: "school_Name", width: 22 },
  { header: "School Code", key: "schoolCode", width: 14 },
  { header: "School Cluster Name", key: "cluster_School_Name", width: 20 },
  { header: "Cluster UDISE Code", key: "cluster_UDISE_Code", width: 18 },
  { header: "Session", key: "session", width: 12 },
  { header: "Status - Present/Absent", key: "statusPresentAbsent", width: 20 },
  { header: "Attendance Time", key: "attendanceTime", width: 16 },
];

export function generateTeacherAttendanceReport(
  rows: ITeacherTrainingReportRow[],
  fileName = "TeacherTrainingAttendance_Report",
) {
  const headerRow = COLUMNS.map((c) => c.header);
  const dataRows = rows.map((row) => COLUMNS.map((c) => row[c.key] ?? ""));

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  // Column widths (roughly matches the screenshot's column widths)
  worksheet["!cols"] = COLUMNS.map((c) => ({ wch: c.width }));

  // Freeze header row so it stays visible on scroll, matching a typical data-dump sheet
  worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Training Title");

  // XLSX.writeFile handles the blob creation + browser download for you
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

/**
 * ---- OPTIONAL: styled version using `xlsx-js-style` ----
 * Same API as `xlsx`, so this is a near drop-in swap if you want the actual
 * blue/yellow/white/green header bands to render:
 *
 *   npm i xlsx-js-style
 *   import * as XLSX from "xlsx-js-style";
 *
 * const HEADER_FILL_BY_COL_INDEX: Record<number, string> = {
 *   0: "CFE2F3", 1: "CFE2F3", 2: "CFE2F3", 3: "CFE2F3", 4: "CFE2F3", // A-E blue
 *   5: "FFE599", 6: "FFE599", 7: "FFE599",                          // F-H yellow
 *   8: "FFFFFF", 9: "FFFFFF", 10: "FFFFFF", 11: "FFFFFF",           // I-L white
 *   12: "D9EAD3", 13: "D9EAD3", 14: "D9EAD3",                       // M-O green
 * };
 *
 * headerRow.forEach((_, colIndex) => {
 *   const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
 *   worksheet[cellRef].s = {
 *     fill: { fgColor: { rgb: HEADER_FILL_BY_COL_INDEX[colIndex] ?? "FFFFFF" } },
 *     font: { bold: true },
 *     alignment: { horizontal: "center", vertical: "center", wrapText: true },
 *   };
 * });
 */
